import { of, switchMap, merge, distinctUntilChanged, tap, fromEvent, map, filter, BehaviorSubject, finalize, forkJoin } from 'rxjs';

import { replaceViewContainer, renderTemplate } from './view.js';
import { jobsQueueLength$, craftPrompts, submitJob } from './api.js';

let artStylePrompts = [
    ['<none>', ''],
    ['professional photography', 'Professional photographs are characterized by incredibly precise exposure and sharpness, achieving a strikingly detailed and often hyper-realistic aesthetic. Crucially, these images deliberately avoid the common pitfall of ‘plastic skin’ – instead, they prioritize natural skin textures and tones, creating a sense of believable depth and volume.'],
    ['amateur photography', 'Amateur photographs frequently display a characteristic softness, slightly inconsistent exposure, and a warmer, less-saturated tone, often showcasing minor technical imperfections. These images possess a charming, raw quality, reflecting a genuine, personal perspective rather than a polished, professionally executed result.'],
    ['medival painting', 'Medieval paintings possess a strikingly flat, symbolic quality, prioritizing religious narrative over realistic representation. These images are characterized by luminous, often jewel-toned colors, applied in thick, gilded layers, creating a stylized and highly embellished aesthetic.'],
    ['renaissance painting', 'Renaissance paintings showcase a dramatic shift towards realism, achieved through the innovative use of techniques like oil paint, fresco, and tempera, resulting in intensely detailed and luminous images. Central to the Renaissance aesthetic was the masterful application of perspective and chiaroscuro, creating a profound sense of depth and volume—a stark contrast to the flatter, more symbolic depictions favored by earlier artists like Bosch.”'],
    ['futurism paintings', 'Futurism paintings, as defined by the provided sources, fundamentally sought to capture the dynamism and energy of the modern world, rejecting traditional artistic conventions in favor of a radical embrace of technology, speed, and industrialization. This is achieved through fragmented forms, multiple perspectives, bold colors – often primaries – and techniques like blurring and repetition, aiming not just to depict a scene, but to evoke the feeling of movement and velocity, mirroring the sensory experience of encountering a fast-paced, technologically advanced society.'],
];

export function createCraftView() {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('craft-view');
            replaceViewContainer(root);
            let craftForm = document.forms.craftForm;
            let submitting$ = new BehaviorSubject(false);

            let artStyleCtrl = root.querySelector('select[name=artStyle]');
            for (let [name, prompt] of artStylePrompts) {
                let opt = document.createElement('option');
                opt.textContent = name;
                opt.value = prompt;
                artStyleCtrl.appendChild(opt);
            }
            
            let queueCounter$ = jobsQueueLength$.pipe(
                distinctUntilChanged(),
                tap(jobCount => root.querySelector('.queue-length').textContent = `${jobCount}`),
            );

            let submitIdea$ = fromEvent(craftForm, 'submit').pipe(
                map(event => {
                    event.preventDefault();
                    return {
                        idea: (craftForm.idea.value || '').trim(),
                        artStyle: artStyleCtrl.value || null,
                        promptCount: parseInt(craftForm.promptCount.value || '3'),
                    };
                }),
                filter(craft => craft.idea && craft.promptCount > 0 && craft.promptCount <= 7),
                switchMap(craft => {
                    submitting$.next(true);
                    let promptsContainer = root.querySelector('.suggested-prompts');
                    promptsContainer.innerHTML = '';
                    return craftPrompts({
                        idea: craft.idea,
                        art_style: craft.artStyle,
                        prompt_count: craft.promptCount,
                    }).pipe(
                        tap(prompts => {
                            promptsContainer.innerHTML = '';
                            for (let prompt of prompts) {
                                let suggestedPromptEl = renderTemplate('suggested-prompt-widget');
                                suggestedPromptEl.setAttribute('data-prompt', prompt.prompt);
                                suggestedPromptEl.setAttribute('data-negative-prompt', prompt.negative_prompt);
                                let promptEl = suggestedPromptEl.querySelector('.prompt');
                                promptEl.textContent = `✅ ${prompt.prompt}`;
                                if (prompt.negative_prompt) {
                                    let negativePromptEl = suggestedPromptEl.querySelector('.negative-prompt');
                                    negativePromptEl.classList.remove('hidden');
                                    negativePromptEl.textContent = `❌ ${prompt.negative_prompt}`;
                                }
                                promptsContainer.appendChild(suggestedPromptEl);
                            }
                            root.querySelector('.suggested-prompts-container').classList.remove('hidden');
                        }),
                        finalize(() => submitting$.next(false)),
                    );
                }),
            );
            
            let inputsDisabled$ = submitting$.pipe(
                distinctUntilChanged(),
                tap(disabled => {
                    for (let el of root.querySelectorAll('textarea, input, select, button')) {
                        el.disabled = disabled;
                    }
                }),
            );

            let suggestedPromptsForm = document.forms.suggestedPromptsForm;
            let submitGenerate$ = fromEvent(suggestedPromptsForm, 'submit').pipe(
                map(event => {
                    event.preventDefault();
                    let prompts = [];
                    for (let sgPromptEl of root.querySelectorAll('.suggested-prompts .suggested-prompt-widget')) {
                        let selected = sgPromptEl.querySelector('.prompt-selected').checked;
                        if (!selected) {
                            continue;
                        }
                        prompts.push({
                            prompt: sgPromptEl.getAttribute('data-prompt'),
                            negativePrompt: sgPromptEl.getAttribute('data-negative-prompt'),
                        });
                    }
                    return {
                        prompts,
                        aspectRatio: suggestedPromptsForm.aspectRatio.value,
                        nsfw: !!suggestedPromptsForm.nsfw.checked
                    };
                }),
                filter(form => form.prompts.length > 0),
                switchMap(form => {
                    submitting$.next(true);
                    return forkJoin(form.prompts.map(p => submitJob({
                        prompt: p.prompt,
                        negative_prompt: p.negativePrompt,
                        aspect_ratio: form.aspectRatio,
                        nsfw: form.nsfw,
                    }))).pipe(
                        tap(() => craftForm.reset()),
                        finalize(() => submitting$.next(false)),
                    );
                }),
            );

            return merge(
                queueCounter$,
                submitIdea$,
                inputsDisabled$,
                submitGenerate$,
            );
        }),
    );
}
